import { z } from "zod";
import {
  DEFAULT_COMMITMENT_YEARS,
  DEFAULT_HORIZON_YEARS,
  MAX_COMMITMENT_YEARS,
  MAX_HORIZON_YEARS,
  MIN_COMMITMENT_YEARS,
  MIN_HORIZON_YEARS,
} from "../constants.js";
import {
  booleanQuerySchema,
  currencyCodeSchema,
  isoDateTimeSchema,
  searchQuerySchema,
  userRefSchema,
} from "./common.js";
import { scenarioSummarySchema } from "./scenario.js";

/**
 * Products are the container and the vocabulary — they hold no prices of their
 * own (docs/03-domain-model.md#product). So this file is mostly identity,
 * defaults that a new scenario inherits, and who may change them.
 */

export const productStatusSchema = z.enum(["DRAFT", "ACTIVE", "ARCHIVED"]);
export type ProductStatusName = z.infer<typeof productStatusSchema>;

export const memberRoleSchema = z.enum(["OWNER", "EDITOR", "VIEWER"]);
export type MemberRoleName = z.infer<typeof memberRoleSchema>;

export const productMemberSchema = z.object({
  userId: z.string(),
  user: userRefSchema,
  role: memberRoleSchema,
  createdAt: isoDateTimeSchema,
});
export type ProductMemberDto = z.infer<typeof productMemberSchema>;

/**
 * Decided once on the server and sent with the resource, for the same reason as
 * `scenarioAbilitiesSchema`: the button the client hides and the guard the
 * server enforces are derived from one expression, not two.
 */
export const productAbilitiesSchema = z.object({
  /** Edit identity, defaults, membership and the baseline pointer. */
  canManage: z.boolean(),
  canArchive: z.boolean(),
  /** Transfer ownership — admins only. */
  canTransfer: z.boolean(),
  /** Anyone allow-listed may build their own scenarios against any product. */
  canCreateScenario: z.boolean(),
});
export type ProductAbilities = z.infer<typeof productAbilitiesSchema>;

export const productRowSchema = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  currencyCode: z.string(),
  defaultHorizonYears: z.number().int(),
  defaultCommitmentYears: z.number().int(),
  status: productStatusSchema,
  ownerId: z.string(),
  owner: userRefSchema,
  baselineScenarioId: z.string().nullable(),
  /** Every scenario on the product the caller is allowed to see. */
  scenarioCount: z.number().int(),
  /** Of those, the ones they wrote. Drives the "yours" filter in the list. */
  myScenarioCount: z.number().int(),
  abilities: productAbilitiesSchema,
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
  archivedAt: isoDateTimeSchema.nullable(),
});
export type ProductRow = z.infer<typeof productRowSchema>;

export const productDetailSchema = productRowSchema.extend({
  members: z.array(productMemberSchema),
  /** The KPIs of the scenario new forks start from; null until it has been saved. */
  baselineSummary: scenarioSummarySchema.nullable(),
  /** True once any scenario exists, which is what freezes `currencyCode`. */
  currencyLocked: z.boolean(),
});
export type ProductDetail = z.infer<typeof productDetailSchema>;

/* ---------------------------------------------------------------- requests */

export const createProductSchema = z.object({
  name: z.string().trim().min(1, "Give the product a name.").max(80),
  description: z.string().trim().max(1000).nullish(),
  currencyCode: currencyCodeSchema,
  defaultHorizonYears: z.coerce
    .number()
    .int()
    .min(MIN_HORIZON_YEARS)
    .max(MAX_HORIZON_YEARS)
    .default(DEFAULT_HORIZON_YEARS),
  defaultCommitmentYears: z.coerce
    .number()
    .int()
    .min(MIN_COMMITMENT_YEARS)
    .max(MAX_COMMITMENT_YEARS)
    .default(DEFAULT_COMMITMENT_YEARS),
  status: productStatusSchema.default("ACTIVE"),
});
export type CreateProductRequest = z.infer<typeof createProductSchema>;

/**
 * Partial by convention (docs/06-api.md#conventions): an omitted field is
 * untouched, an explicit `null` clears a nullable one.
 *
 * `slug` is absent on purpose. It is minted from the name at creation and then
 * frozen — a product rename should not break every link anybody has shared.
 */
export const updateProductSchema = z
  .object({
    name: z.string().trim().min(1).max(80),
    description: z.string().trim().max(1000).nullable(),
    /** Rejected once any scenario exists; see docs/06-api.md#products. */
    currencyCode: currencyCodeSchema,
    defaultHorizonYears: z.coerce.number().int().min(MIN_HORIZON_YEARS).max(MAX_HORIZON_YEARS),
    defaultCommitmentYears: z.coerce
      .number()
      .int()
      .min(MIN_COMMITMENT_YEARS)
      .max(MAX_COMMITMENT_YEARS),
    status: productStatusSchema,
    /** Point new forks at a different scenario; `null` clears the pointer. */
    baselineScenarioId: z.string().min(1).nullable(),
  })
  .partial();
export type UpdateProductRequest = z.infer<typeof updateProductSchema>;

export const addMemberSchema = z.object({
  email: z.string().trim().toLowerCase().email("Enter a valid email address.").max(254),
  role: memberRoleSchema.default("VIEWER"),
});
export type AddMemberRequest = z.infer<typeof addMemberSchema>;

export const transferProductSchema = z.object({
  ownerId: z.string().min(1),
});
export type TransferProductRequest = z.infer<typeof transferProductSchema>;

export const productListQuerySchema = z.object({
  status: productStatusSchema.optional(),
  q: searchQuerySchema,
  /** Products the caller owns or is a member of. */
  mine: booleanQuerySchema,
});
export type ProductListQuery = z.infer<typeof productListQuerySchema>;
